import React, { memo, type PropsWithChildren } from 'react';

import { shallowEqualObjects } from 'shallow-equal';

import FeatureGates from '@atlaskit/feature-gate-js-client';

import { HeadlessProbeChatInputPlacement } from './headless-probe-chat-input-placement';
import { RemoteChatInputPlacement } from './remote-chat-input-placement';

type ChatInputPlacementContainerProps = {
	shouldShow: boolean;
	fullScreen?: boolean;
};

const ClientSideOnly = ({ children }: PropsWithChildren): React.JSX.Element | null =>
	typeof window !== 'undefined' ? <>{children}</> : null;

const ChatInputPlacementContainerComponent = ({
	shouldShow,
	fullScreen,
}: ChatInputPlacementContainerProps): React.JSX.Element | null => {
	if (!shouldShow) {
		return null;
	}

	return (
		<ClientSideOnly>
			{fullScreen ? (
				<RemoteChatInputPlacement placementId="rovo-chat-input-full-page" fullScreen={fullScreen} />
			) : (
				<HeadlessProbeChatInputPlacement />
			)}
		</ClientSideOnly>
	);
};

// Experiment-only: absorb parent render waves when placement props are stable.
const MemoizedChatInputPlacementContainer = memo(
	ChatInputPlacementContainerComponent,
	shallowEqualObjects,
);

export const ChatInputPlacementContainer = (
	props: ChatInputPlacementContainerProps,
): React.JSX.Element | null => {
	return FeatureGates.getExperimentValue(
		'rovo_ttai_segment_render_stabilization',
		'isEnabled',
		false,
	) ? (
		<MemoizedChatInputPlacementContainer {...props} />
	) : (
		<ChatInputPlacementContainerComponent {...props} />
	);
};
